import {
  config as domainConfig,
  FormSubmissionType,
  UserReturnType,
} from "@validereinc/domain";
import { dateFormatter, datetimeFormatter } from "@validereinc/utilities";
import axios from "axios";
import { isValid, parse } from "date-fns";
import { startCase } from "es-toolkit/compat";
import { AsyncZipDeflate, Zip } from "fflate";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import config from "../../config";
import { ExceptionUtils } from "../../utils/exception";
import FileService from "../Services/FileService";
import {
  getFetchedFileUriCacheKey,
  getFileEntriesFromAnswerValue,
} from "./exportFormSubmissionPDF.helpers";

const HEADER_WIDTH = 250;
const IMAGE_WIDTH = 525 - HEADER_WIDTH;
const FONT_SIZE = 10;

//we can't import the colours from the common-components because it will not be available in the pdf worker
const STATUS_MAPPING = {
  submitted: "#E2F0FD", // colours.colours.status.infoLight,
  validated: "#E1FCEF",
  invalidated: "#FBE2E2", // colours.colours.status.errorLight,
  pending: "#FFE9E1", // colours.colours.status.warningLight,
  draft: "#F2F4F5", // colours.colours.surface[100],
};

const primaryHeaderStyle = {
  fillColor: "#3B484F", // colours.colours.text[800],
  colSpan: 2,
  color: "white",
  bold: true,
};

const secondaryHeaderStyle = {
  fillColor: "#4D6670", // colours.colours.text[600],
  colSpan: 2,
  color: "white",
  bold: true,
};

const keyStyle = {
  fillColor: "#F5F6F8", // colours.colours.surface[75],
  fontSize: FONT_SIZE,
};
const valueStyle = { fontSize: FONT_SIZE };

const tableStyle = {
  hLineColor: () => "#E9EBED", // colours.colours.surface[200],
  vLineWidth: () => 0,
  paddingLeft: () => 8,
  paddingRight: () => 8,
  paddingTop: () => 4,
  paddingBottom: () => 4,
};

const getDocDefinition = (content: TDocumentDefinitions["content"]) => ({
  content,
  pageMargins: [18, 18, 18, 60],
  footer: function (currentPage: number, pageCount: number) {
    return {
      margin: [18, 18, 18, 36],
      columns: [
        {
          svg: `<svg width="122" height="16" viewBox="0 0 122 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M18.322 1.03799C19.3962 1.51617 20.0454 2.52577 20.6383 3.50209V3.50114C20.6388 3.50114 21.91 5.59647 23.3675 7.99891C25.0488 10.7702 26.9781 13.9502 27.4907 14.794H21.6168C21.2826 13.8842 19.7293 9.13424 18.6984 5.98175C18.3752 4.99328 18.1033 4.16187 17.9364 3.65514C17.8792 3.48308 17.639 3.48308 17.5809 3.65514L14.3827 13.1398C14.2917 13.4117 14.1669 13.6655 14.0168 13.8984V13.8956C13.7494 14.3101 13.3939 14.6656 12.9754 14.9232C12.0157 15.5155 10.7407 15.6125 9.71064 15.1543C8.63644 14.6761 7.98724 13.6665 7.39432 12.6902C7.39403 12.6899 6.82472 11.7515 6.01133 10.4109C4.207 7.43697 1.20158 2.48341 0.541992 1.39829H6.41488C6.74912 2.308 8.30236 7.05779 9.33326 10.2103C9.65653 11.1988 9.92843 12.0303 10.0953 12.5371C10.1525 12.7092 10.3927 12.7092 10.4508 12.5371L13.65 3.05243C13.741 2.78054 13.8658 2.52672 14.0159 2.29381V2.29666C14.2832 1.88217 14.6388 1.52663 15.0572 1.269C16.017 0.676746 17.2919 0.579779 18.322 1.03799ZM74.5157 1.3494H78.8006C82.6995 1.3494 85.6401 4.24909 85.6401 8.0956C85.6401 11.9421 82.6995 14.8418 78.8006 14.8418H74.5157V1.3494ZM76.0991 13.2906H78.8006C81.8353 13.2906 84.0382 11.1156 84.0382 8.09468C84.0382 5.07374 81.8353 2.88115 78.8006 2.88115H76.0991V13.2906ZM60.7951 1.36799H59.2117V14.8234H67.5599V13.2916H60.7951V1.36799ZM71.5113 1.36799H69.928V14.8234H71.5113V1.36799ZM87.6796 14.8234H97.0941V13.2916H89.263V8.83427H96.3592V7.33861H89.263V2.88217H97.0941V1.36799H87.6796V14.8234ZM121.292 1.36799V2.88217H113.461V7.33861H120.557V8.83427H113.461V13.2916H121.292V14.8234H111.877V1.36799H121.292ZM44.8042 1.36799L40.3144 12.6484L35.8412 1.36799H34.1064L39.4244 14.8234H41.1417L46.5233 1.36799H44.8042ZM45 14.8234L50.3817 1.36799H52.098L57.417 14.8234H55.6812L54.3582 11.4878H48.0458L46.7182 14.8234H45ZM51.2089 3.543L48.6487 9.9736H53.759L51.2089 3.543ZM106.208 9.58572C108.271 9.28122 109.656 7.65413 109.656 5.53373C109.656 4.41476 109.272 3.3828 108.574 2.62664C107.813 1.80291 106.75 1.36699 105.5 1.36699H99.8998V14.8224H101.483V2.86265H105.5C107.412 2.86265 108.091 4.29167 108.091 5.51522C108.091 7.13768 106.966 8.18631 105.224 8.18631H103.605C103.378 8.18631 103.248 8.44453 103.381 8.62779L107.905 14.8233H109.915L106.096 9.60423C106.153 9.59497 106.191 9.58849 106.207 9.58572H106.208Z" fill="#73939D"/>
          </svg>`,
          fit: [76.27, 10],
        },
        {
          text: `${currentPage}/${pageCount}`,
          alignment: "right",
          style: {
            color: "#73939D", // colours.colours.text[400],
            fontSize: 10,
          },
        },
      ],
    };
  },
});

/**
 * @param arrayBuffer Determines the file type of a blob based on its signature.
 *
 * @returns The file type of the blob.
 */
const determineFileType = (arrayBuffer: ArrayBuffer) => {
  const uint8arr = new Uint8Array(arrayBuffer);

  const len = 4;
  if (uint8arr.length >= len) {
    const signatureArr = new Array(len);

    for (let i = 0; i < len; i++)
      signatureArr[i] = new Uint8Array(arrayBuffer)[i].toString(16);

    const signature = signatureArr.join("").toUpperCase();

    switch (signature) {
      case "89504E47":
        return "image/png";
      case "47494638":
        return "image/gif";
      case "25504446":
        return "application/pdf";
      case "FFD8FFDB":
      case "FFD8FFE0":
      case "FFD8FFE1":
        return "image/jpeg";
      case "504B0304":
        return "application/zip";
      default:
        return null;
    }
  }
  return null;
};

/**
 * Formats a value based on its type.
 *
 * @param value - The value to format
 * @returns The formatted value
 */
const formatValue = ({
  value,
}: {
  value?: string | number | Array<string | number> | Date;
}) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "number") {
    return `${value}`;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value instanceof Date) {
    return dateFormatter(
      new Date(value.valueOf() + value.getTimezoneOffset() * 60 * 1000)
    );
  }

  if (isValid(parse(value.substring(0, 10), "yyyy-MM-dd", new Date()))) {
    const dateValue = new Date(value);

    const timezoneAwareDate = value.includes("Z")
      ? dateValue
      : new Date(
          dateValue.valueOf() + dateValue.getTimezoneOffset() * 60 * 1000
        );

    return value.includes("Z")
      ? datetimeFormatter(timezoneAwareDate)
      : dateFormatter(timezoneAwareDate);
  }

  return value;
};

/**
 * Error handling wrapper around generating a PDF
 * Caution - only run one of these at a time - they will all reject at once if any error is detected
 */
export const getPdfPromise = async (
  docDefinition: TDocumentDefinitions,
  {
    onSuccess,
    onError,
    fileName = `${Date.now().toString()}.pdf`,
    mode = "download",
  }: {
    mode?: "download" | "getBuffer";
    fileName?: string;
    onSuccess?: (
      resolve: (value: unknown) => void,
      reject: (value: unknown) => void
    ) => void;
    onError?: (
      resolve: (value: unknown) => void,
      reject: (value: unknown) => void,
      error: PromiseRejectionEvent
    ) => void;
  } = {}
): Promise<unknown> => {
  const pdfMake = (await import("pdfmake/build/pdfmake")).default;
  const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;

  return new Promise((resolve, reject) => {
    /** No other way to capture this exception https://github.com/bpampuch/pdfmake/issues/826 */
    const pdfExceptionListener = (error: PromiseRejectionEvent) => {
      onError?.(resolve, reject, error);
      //window not available in js workers
      if (typeof window !== "undefined") {
        window.removeEventListener("unhandledrejection", pdfExceptionListener);
      }
    };
    //window not available in js workers
    if (typeof window !== "undefined") {
      window.addEventListener("unhandledrejection", pdfExceptionListener);
    }

    const successCallback = () => {
      onSuccess?.(resolve, reject);
      //window not available in js workers
      if (typeof window !== "undefined") {
        window.removeEventListener("unhandledrejection", pdfExceptionListener);
      }
    };

    /** https://stackoverflow.com/a/79051671 */
    const pdf = pdfMake.createPdf(
      docDefinition,
      undefined,
      undefined,
      pdfFonts.vfs as unknown as Record<string, string>
    );

    switch (mode) {
      case "download":
        return pdf.download(fileName, successCallback);
      case "getBuffer":
        return pdf.getBuffer(successCallback);
    }
  });
};

export const doesFilePrintToPDF = (image: ArrayBuffer) => {
  const docDefinition = getDocDefinition([{ image } as any]);

  return getPdfPromise(docDefinition as any, {
    onSuccess: (resolve) => resolve(true),
    onError: (resolve) => resolve(false),
    mode: "getBuffer",
  });
};

const compressBlob = async (blob: Blob) => {
  const bitmap = await createImageBitmap(blob);

  //300DPI, A4 paper, cut it in half since image can only takes up half the page
  const maxHeight = 3508 / 2;
  const maxWidth = 2480 / 2;
  const { width, height } = bitmap;
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);
  const canvas = new OffscreenCanvas(width * scale, height * scale);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  const scaledDownBlob = await canvas.convertToBlob({
    type: "image/jpeg",
    quality: 0.7,
  });
  return await scaledDownBlob.arrayBuffer();
};

/**
 * Generates PDF content of a form submission.
 * @returns the PDF content and file name.
 */
export const generatePDFContent = async (
  /** the form submission to export */
  formSubmission: FormSubmissionType,
  {
    includeEmptyAnswers = false,
    showUpdatedAt = false,
    metaUserDataMap = {},
    bearerToken,
  }: {
    /** should submission questions that haven't been answered be included in the export? */
    includeEmptyAnswers?: boolean;
    /** show the updated at date? */
    showUpdatedAt?: boolean;
    /** a map of user ID's to user data, to enrich meta user information */
    metaUserDataMap?: Record<string, UserReturnType>;
    /** the bearer token to use in an Authorization header for data platform requests */
    bearerToken?: string;
  } = {}
) => {
  const fileName = `${formSubmission.form_schema?.name ? `${formSubmission.form_schema?.name} - ` : ""}${formSubmission.id.substring(0, 7)}`;
  const content: any[] = [];

  content.push({
    table: {
      headerRows: 1,
      widths: [HEADER_WIDTH, "*"],
      body: [
        [
          {
            ...primaryHeaderStyle,
            text: fileName,
          },
          "",
        ],
        [
          { ...keyStyle, text: "Created At" },
          {
            ...valueStyle,
            text: formatValue({ value: formSubmission.updated_at }),
          },
        ],
        [
          { ...keyStyle, text: "Created By" },
          {
            ...valueStyle,
            text:
              metaUserDataMap[formSubmission.created_by ?? ""]?.name ??
              formSubmission.created_by,
          },
        ],
        ...(showUpdatedAt
          ? [
              [
                { ...keyStyle, text: "Updated At" },
                {
                  ...valueStyle,
                  text: formatValue({ value: formSubmission.updated_at }),
                },
              ],
            ]
          : []),
        [
          { ...keyStyle, text: "Status" },
          {
            ...valueStyle,
            fillColor: STATUS_MAPPING[formSubmission.status],
            text: startCase(formSubmission.status),
          },
        ],
      ],
    },
    layout: tableStyle,
  });

  content.push("\n");

  const docDefinition = getDocDefinition(content);

  const displaySections = Object.entries(formSubmission.answers).map(
    ([sectionId, answersArray]) => {
      const section = formSubmission.form_schema?.config.sections.find(
        ({ id }) => id === sectionId
      );

      const questions = section?.questions.map((questionId) => ({
        ...(formSubmission.form_schema?.config.questions[questionId] ?? {}),
        id: questionId,
      }));

      return {
        ...section,
        answers: answersArray.map((answersObject) =>
          answersObject
            ? questions?.map(({ id, ...restQuestion }) => ({
                ...restQuestion,
                id,
                value:
                  answersObject[id] &&
                  "name" in answersObject[id] &&
                  answersObject[id]?.name
                    ? answersObject[id]?.name
                    : answersObject[id]?.value,
              }))
            : []
        ),
      };
    }
  );

  const fetchImageUri = async (ref: string) => {
    const data = bearerToken
      ? await axios.get(
          config.DATA_PLATFORM_API_URL + `/data/file_download_url/${ref}`,
          {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
            },
          }
        )
      : await FileService.getFileDownloadUrl(ref);
    if (!data)
      return {
        uri: "",
      };

    const {
      data: {
        data: [{ uri }],
      },
    } = data;
    return {
      uri,
    };
  };

  const fetchedImageUris: Record<
    string,
    {
      uri: string;
    }
  > = {};
  const fetchImagesPromiseArray: Array<Promise<void>> = [];
  for (const section of displaySections) {
    const { answers } = section;
    if (answers?.length) {
      answers.forEach((answer, index) => {
        const currentSetOfAnswers = answer ?? [];
        for (const answer of currentSetOfAnswers) {
          const { value } = answer;
          const fileEntries = getFileEntriesFromAnswerValue(value);

          if (!fileEntries) continue;

          fileEntries.forEach((file, fileIndex) => {
            fetchImagesPromiseArray.push(
              fetchImageUri(file.ref)
                .then((result) => {
                  fetchedImageUris[
                    getFetchedFileUriCacheKey({
                      sectionId: section.id,
                      answerId: answer.id,
                      answerInstanceIndex: index,
                      fileIndex,
                    })
                  ] = result;
                })
                .catch((error) => {
                  ExceptionUtils.reportException(error, "error", {
                    hint: `Failed to fetch image URI for form submission ${formSubmission.id}. Ref: ${file.ref}`,
                  });
                })
            );
          });
        }
      });
    }
  }

  await Promise.all(fetchImagesPromiseArray);

  for (const section of displaySections) {
    const { name, answers } = section;

    if (answers?.length) {
      const sectionTable: {
        headerRows: number;
        widths: Array<string | number>;
        body: Array<string | Record<string, any>>;
      } = {
        /** Changed this to zero, because it would always propgate the first header to new pages */
        /** We might be able to avoid this by structuring each question as a different table */
        headerRows: 0,
        widths: [HEADER_WIDTH, "*"],
        body: [],
      };

      for (let i = 0; i < answers.length; i++) {
        sectionTable.body.push([
          {
            ...secondaryHeaderStyle,
            text: answers?.length > 1 ? `${name} ${i + 1}` : name,
          },
          "",
        ]);

        const currentSetOfAnswers = answers[i] ?? [];
        for (const answer of currentSetOfAnswers) {
          const { value } = answer;

          /** Skip answers which have an equation and are empty */
          if (
            "equation" in answer &&
            !!answer.equation &&
            (value === null || value === undefined || value === "")
          ) {
            continue;
          }

          if (!("prompt" in answer) || !answer.prompt) continue;
          const { prompt } = answer;

          if (
            !includeEmptyAnswers &&
            (value === null || value === undefined || value === "")
          )
            continue;

          const fileEntries = getFileEntriesFromAnswerValue(value);

          if (fileEntries) {
            /** Accumulates a preview image, or fallback text, per file in this answer */
            const fileStackContent: Array<Record<string, unknown>> = [];

            for (const [fileIndex, file] of fileEntries.entries()) {
              /** Only disambiguate by filename when previewing more than one file */
              const cannotPreviewText =
                fileEntries.length > 1
                  ? `${file.name}: File cannot be previewed.`
                  : "File cannot be previewed.";

              const uri =
                fetchedImageUris[
                  getFetchedFileUriCacheKey({
                    sectionId: section.id,
                    answerId: answer.id,
                    answerInstanceIndex: i,
                    fileIndex,
                  })
                ]?.uri;

              if (!uri) {
                fileStackContent.push({
                  ...valueStyle,
                  text: cannotPreviewText,
                });
                continue;
              }

              //this is necessary because the worker won't be able to exit via the window handlers
              try {
                const res = await fetch(uri);
                const blob = await res.blob();
                const signatureSlice = await blob.slice(0, 8).arrayBuffer();
                const fileType = determineFileType(signatureSlice);

                const isSupportedImageType =
                  fileType && ["image/jpeg", "image/png"].includes(fileType);

                if (!isSupportedImageType) {
                  fileStackContent.push({
                    ...valueStyle,
                    text: cannotPreviewText,
                  });
                  continue;
                }

                const fileArrayBuffer = await compressBlob(blob);
                const canPrintToPDF = await doesFilePrintToPDF(fileArrayBuffer);

                if (!canPrintToPDF) {
                  fileStackContent.push({
                    ...valueStyle,
                    text: cannotPreviewText,
                  });
                  continue;
                }

                fileStackContent.push({
                  image: fileArrayBuffer,
                  width: IMAGE_WIDTH,
                  /** Space out images stacked within the same multi-file answer */
                  ...(fileIndex > 0 ? { margin: [0, 8, 0, 0] } : {}),
                });
              } catch (error) {
                fileStackContent.push({
                  ...valueStyle,
                  text: cannotPreviewText,
                });
              }
            }

            sectionTable.body.push([
              { ...keyStyle, text: prompt },
              { stack: fileStackContent },
            ]);
          } else {
            sectionTable.body.push([
              { ...keyStyle, text: prompt },
              {
                ...valueStyle,
                text: formatValue({
                  value: value as
                    | string
                    | number
                    | Array<string | number>
                    | Date,
                }),
              },
            ]);
          }
        }
      }

      content.push({
        table: sectionTable,
        layout: tableStyle,
      });
    }
  }

  return { docDefinition, fileName };
};

/**
 * Generates and downloads a PDF of a form submission.
 * @returns the file name that was exported. triggers a download of the created PDF.
 */
export const exportFormSubmissionAsPDF = async (
  /** the form submission to export */
  formSubmission: FormSubmissionType,
  {
    includeEmptyAnswers = false,
    showUpdatedAt = false,
    metaUserDataMap = {},
  }: {
    /** should submission questions that haven't been answered be included in the export? */
    includeEmptyAnswers?: boolean;
    /** show the updated at date? */
    showUpdatedAt?: boolean;
    /** a map of user ID's to user data, to enrich meta user information */
    metaUserDataMap?: Record<string, UserReturnType>;
  } = {}
) => {
  const { docDefinition, fileName } = await generatePDFContent(formSubmission, {
    includeEmptyAnswers,
    showUpdatedAt,
    metaUserDataMap,
  });

  return getPdfPromise(docDefinition as any, {
    onSuccess: (resolve) => resolve(`${fileName}.pdf`),
    onError: (_, reject, error) => reject(error.reason),
    fileName: `${fileName}.pdf`,
    mode: "download",
  });
};

export const FORM_SUBMISSIONS_PDF_BATCH_SIZE = 75;
/**
 * Bulk export form submissions as PDFs through a provided JS worker
 * @param worker - the worker to use to generate the PDFs
 * @param formSubmissions - the form submissions to export
 * @param onProgress - a callback to call with the progress of the export
 * @returns void
 */
export const bulkExportFormSubmissionAsPDF = async (
  worker: Worker,
  formSubmissions: FormSubmissionType[],
  onProgress: (progress: number, blobUrl: string) => void
) => {
  const chunks: Uint8Array[] = [];
  const zipStream = new Zip((err, data, final) => {
    if (err) {
      ExceptionUtils.reportException(err, "error", {
        hint: `Failed to zip stream form submission PDFs`,
      });
      return;
    }

    if (data) chunks.push(data);

    if (final) {
      const blob = new Blob(chunks as BlobPart[], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const formSchemaName = formSubmissions[0].form_schema?.name;
      a.download = `${formSchemaName}: Submissions.zip`;
      a.click();
      onProgress(100, url);
    }
  });

  let numberSubmissionsProcessed = 0;
  let currentBatch = 0;
  const runBatch = (batchData, batchIndex) => {
    return new Promise<void>((resolve, reject) => {
      if (worker) {
        worker.onmessage = (e) => {
          const { type, pdfFiles } = e.data;

          if (type === "progress") {
            numberSubmissionsProcessed++;
            onProgress(
              Math.round(
                (numberSubmissionsProcessed / formSubmissions.length) * 100
              ),
              ""
            );
          }

          if (type === "completed") {
            for (const [filename, content] of Object.entries(pdfFiles)) {
              const file = new AsyncZipDeflate(filename, {
                level: 6,
              });
              zipStream.add(file);
              file.push(content as Uint8Array<ArrayBufferLike>, true);
            }
            resolve();
          }
        };

        worker.postMessage({
          batchData,
          batchIndex,
          bearerToken: domainConfig.bearer,
        });
      } else {
        reject(new Error("Worker is not available"));
      }
    });
  };

  while (
    currentBatch * FORM_SUBMISSIONS_PDF_BATCH_SIZE <
    formSubmissions.length
  ) {
    const start = currentBatch * FORM_SUBMISSIONS_PDF_BATCH_SIZE;
    const end = start + FORM_SUBMISSIONS_PDF_BATCH_SIZE;
    const batchData = formSubmissions.slice(start, end);

    await runBatch(batchData, currentBatch);

    // yield to event loop for GC
    await new Promise((r) => setTimeout(r, 0));

    currentBatch++;
  }

  worker.terminate();

  zipStream.end();
};
